import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../Hooks/useAuth';
import UseAxios from '../../Api/http/http';
import { serviceName } from '../../Api/resources/serviceConstants';
import { NOTIFICATON } from '../../Api/resources/endPoint';
import './ApprovalNotification.scss';

const ApprovalNotification = ({ onClose, onStatsFetch }) => {
  const { userProfile } = useAuth();
  const [approvalStats, setApprovalStats] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  const fetchApprovalStats = async () => {
    try {
      const reqConfig = {
        url: `${NOTIFICATON}/approval-pending-stats?emp_id=${userProfile?.employeeId || ''}`,
        method: 'get',
        headers: { 'content-type': 'application/json' },
        service: serviceName.PROJECT_MS,
      };
      const response = await UseAxios(reqConfig);
      if (response?.data?.success) {
        const stats = response.data?.data || null;
        setApprovalStats(stats);
      }
    } catch (error) {
      console.error('Error fetching approval stats:', error);
    }
  };

  useEffect(() => {
    if (userProfile?.employeeId) {
      fetchApprovalStats();
    }
  }, [userProfile]);

  const filteredSections = useMemo(() => {
    if (!approvalStats?.sections) return [];
    return approvalStats.sections.filter((sec) => {
      if (!sec.open_request || Number(sec.open_request) <= 0) {
        return false;
      }
      const lowerName = sec.section.toLowerCase();
      const isCR = lowerName.includes('cr approval');
      if (isCR) {
        return userProfile?.permissions?.['project:change-request']?.length > 0;
      }
      if (lowerName.includes('project status change')) {
        return (
          userProfile?.permissions?.[
            'delivery:project-overview:project-status'
          ]?.length > 0
        );
      }
      if (lowerName.includes('timeline date')) {
        return (
          userProfile?.permissions?.[
            'delivery:project-overview:timeline-change-request'
          ]?.length > 0
        );
      }
      if (lowerName.includes('resource loading')) {
        return (
          userProfile?.permissions?.['delivery:resource-loading-plan']
            ?.length > 0
          || userProfile?.permissions?.[
            'delivery:resource-loading-approval-history'
          ]?.length > 0
        );
      }
      if (lowerName.includes('shift allowance')) {
        return (
          userProfile?.permissions?.[
            'resource:shift-allowance-approval-history'
          ]?.length > 0
          || userProfile?.permissions?.['resource:shift-allowance']?.length > 0
        );
      }
      if (lowerName.includes('billing report')) {
        return userProfile?.permissions?.[
          'mis:billing-report-approval-history'
        ]?.length > 0;
      }
      if (lowerName.includes('staffing request')) {
        return (
          userProfile?.permissions?.['delivery:staffing-request']?.length > 0
        );
      }
      return true;
    });
  }, [approvalStats?.sections, userProfile]);

  const filteredTotal = useMemo(() => {
    return filteredSections.reduce((acc, cur) => acc + Number(cur.open_request || 0), 0);
  }, [filteredSections]);

  useEffect(() => {
    if (onStatsFetch && approvalStats) {
      onStatsFetch(filteredTotal);
    }
  }, [filteredTotal, onStatsFetch, approvalStats]);

  const handleRedirect = (sectionName, projectId, projectName = '', filterProjectId = '') => {
    if (onClose) onClose();
    const lowerName = sectionName.toLowerCase();
    const queryParams = new URLSearchParams();
    if (projectId) queryParams.set('projectId', projectId);
    if (projectName) queryParams.set('projectName', projectName);
    const queryString = queryParams.toString();
    const query = queryString ? `?${queryString}` : '';

    if (lowerName.includes('cr approval')) {
      queryParams.set('view', 'approval-history');
      window.location.href = `/projects/change-request?${queryParams.toString()}`;
    } else if (lowerName.includes('timeline date')) {
      window.location.href = `/projects/change-request/timeline${query}`;
    } else if (lowerName.includes('project status change')) {
      const projParams = new URLSearchParams();
      if (projectId) projParams.set('projectId', projectId);
      if (projectName) projParams.set('projectName', projectName);
      projParams.set('fromNotification', 'true');
      window.location.href = `/delivery/project-details/request-history?${projParams.toString()}`;
    } else if (lowerName.includes('resource loading')) {
      window.location.href = `/delivery/project-details/resource-loading-plan/approval-history${query}`;
    } else if (lowerName.includes('shift allowance')) {
      const shiftAllowanceParams = new URLSearchParams();
      if (projectId) shiftAllowanceParams.set('allowanceRequestId', projectId);
      shiftAllowanceParams.set('requestStatus', 'Open');
      shiftAllowanceParams.set('fromNotification', 'true');
      window.location.href = `/resource/shift-allowance/approval-history?${shiftAllowanceParams.toString()}`;
    } else if (lowerName.includes('billing report')) {
      const billingReportParams = new URLSearchParams();
      if (projectId) billingReportParams.set('billingReportId', projectId);
      if (filterProjectId) billingReportParams.set('projectId', filterProjectId);
      if (projectName) billingReportParams.set('projectName', projectName);
      billingReportParams.set('requestStatus', 'Open');
      billingReportParams.set('fromNotification', 'true');
      window.location.href = `/mis/billing-report/approval-history?${billingReportParams.toString()}`;
    } else if (lowerName.includes('staffing request')) {
      window.location.href = `/delivery/staffing-request${query}`;
    } else {
      window.location.href = `/projects/change-request${query}`;
    }
  };

  const handleItemClick = (sec) => {
    const hasProjects = sec.projects && sec.projects.length > 0;

    if (hasProjects) {
      setExpandedSection(expandedSection === sec.section ? null : sec.section);
    } else {
      handleRedirect(sec.section, null);
    }
  };

  if (!approvalStats || filteredTotal === 0) return null;

  return (
    <div className="approval-pending-container">
      <div className="approval-pending-header">
        <span className="approval-pending-title">Approval Pending</span>
        <span className="approval-pending-total">{filteredTotal} total</span>
      </div>
      <div className="approval-pending-grid">
        {filteredSections.map((sec, idx) => {
          const isExpanded = expandedSection === sec.section;
          return (
            <div key={idx} className="approval-pending-section-group">
              <div
                className={`approval-pending-card ${isExpanded ? 'approval-pending-card--expanded' : ''}`}
                onClick={() => handleItemClick(sec)}
              >
                <span className="approval-pending-section-name">{sec.section}</span>
                <span className="approval-pending-section-badge">{sec.open_request || 0}</span>
              </div>
              {isExpanded && sec.projects && (
                <div className="approval-pending-projects-list">
                  {sec.projects.map((proj) => (
                    <div
                      key={proj.id}
                      className="approval-pending-project-item"
                      onClick={() => handleRedirect(
                        sec.section,
                        proj.id,
                        proj.name,
                        proj.projectId,
                      )}
                    >
                      <span className="approval-pending-project-name" title={proj.name}>
                        {proj.name}
                      </span>
                      {proj.open_request !== undefined && proj.open_request !== null && (
                        <span className="approval-pending-project-badge">{proj.open_request}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="notifications-divider" style={{ marginTop: '15px', marginBottom: '0px' }} />
    </div>
  );
};

ApprovalNotification.propTypes = {
  onClose: PropTypes.func,
  onStatsFetch: PropTypes.func,
};

export default ApprovalNotification;
